import { isVerticalCategory } from '@kijiji/category'
import {
  type R2SPlacement,
  AuthModalVariant,
  GetR2SStateDocument,
  useGetR2SStateQuery,
} from '@kijiji/generated/graphql-types'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useMemo } from 'react'

import { isUserAuthenticated } from '@/features/auth/constants/user'
import { useSignInModal } from '@/features/auth/hooks/useSignInModal'
import { type GenericFullListing } from '@/features/listing/types/listings'
import { isDealerListing } from '@/features/listing/utils/isDealerListing'
import { isPintListing } from '@/features/listing/utils/isPintListing'
import { isSM360Listing } from '@/features/listing/utils/isSM360Listing'

/**
 * Hook to handle the reply to seller modal
 * It should be used whenever using the R2S feature, so the open modal will implement the correct callback
 */
export const useReplyToSellerModal = () => {
  const { asPath } = useRouter()
  const { status } = useSession()
  const { openSignInModal } = useSignInModal()

  const { data, client } = useGetR2SStateQuery({ fetchPolicy: 'cache-only' })

  const handleCloseModal = () => {
    client.cache.writeQuery({
      query: GetR2SStateDocument,
      data: {
        r2sModalState: { __typename: 'ReplyToSellerModalState', isOpen: false, placement: null },
      },
    })
  }

  const handleOpenR2SModal = useMemo(
    () => (listing: GenericFullListing, trackingPlacement?: R2SPlacement) => {
      const isPintAd = isPintListing(listing)
      const isSM360Ad = isSM360Listing(listing)
      const isListingFromDealer = isDealerListing(listing.attributes)
      const isVerticalAd = isVerticalCategory(listing.categoryId)

      const isGuestMessageEnabled = (isPintAd || isSM360Ad || isListingFromDealer) && isVerticalAd

      if (!isUserAuthenticated(status) && !isGuestMessageEnabled) {
        openSignInModal({ modalVariant: AuthModalVariant.LoginToMessage, callbackUrl: asPath })
        return
      }

      client.cache.writeQuery({
        query: GetR2SStateDocument,
        data: {
          r2sModalState: {
            __typename: 'ReplyToSellerModalState',
            isOpen: true,
            placement: trackingPlacement?.toLowerCase() ?? null,
          },
        },
      })
    },
    [asPath, client.cache, openSignInModal, status]
  )

  return {
    handleCloseModal,
    handleOpenModal: handleOpenR2SModal,
    isOpen: !!data?.r2sModalState.isOpen,
    placement: data?.r2sModalState.placement,
  }
}
